import React, { useEffect, useRef, useState } from "react";
import './hero2.css';

const DEFAULT_VIDEO_URL = 'https://www.youtube.com/embed/lxOWww4vfmY?autoplay=1&mute=1&loop=1&playlist=lxOWww4vfmY&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3';
const DEFAULT_POSTER = `${process.env.PUBLIC_URL}/images/hero/lxOWww4vfmY-desktop.webp`;
const DEFAULT_MOBILE_POSTER = `${process.env.PUBLIC_URL}/images/hero/lxOWww4vfmY-mobile.webp`;
const MOBILE_QUERY = '(max-width: 768px)';

const useInView = (ref) => {
    const [inView, setInView] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element || !('IntersectionObserver' in window)) {
            setInView(true);
            return undefined;
        }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setInView(true);
                observer.disconnect();
            }
        }, { threshold: 0.25 });
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);

    return inView;
};

/**
 * Full-screen hero with a background video.
 *
 * The poster image paints immediately. The YouTube/self-hosted video loads once
 * this hero is on screen (no interaction wait). Off-screen heroes stay deferred.
 */
const Hero2 = ({
    heroClass = '',
    videoUrl = DEFAULT_VIDEO_URL,
    mobileVideoUrl = DEFAULT_VIDEO_URL,
    videoSrc,
    mobileVideoSrc,
    poster = DEFAULT_POSTER,
    mobilePoster = DEFAULT_MOBILE_POSTER,
    priority = false,
    children
}) => {
    const containerRef = useRef(null);
    const inView = useInView(containerRef);
    const [videoReady, setVideoReady] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

    useEffect(() => {
        const media = window.matchMedia(MOBILE_QUERY);
        const onChange = (event) => {
            setIsMobile(event.matches);
            setVideoReady(false);
        };
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    const showVideo = inView;
    const selfHostedSrc = isMobile ? (mobileVideoSrc || videoSrc) : videoSrc;
    const videoClass = `${isMobile ? 'mobile-video' : 'desktop-video'} hero-video${videoReady ? ' is-ready' : ''}`;

    return (
        <section ref={containerRef}>
            <div className={`wpo-hero-slider wpo-hero-style-2 ${heroClass}`}>
                <div className="hero-container">
                    <div className="hero-wrapper">
                            <div className="hero-slide">
                                <div className="slide-inner video-slide" style={{ height: '100vh', width: '100vw' }}>
                                    <div className="video-background">
                                        <picture>
                                            <source media={MOBILE_QUERY} srcSet={mobilePoster} />
                                            <img
                                                className="video-poster"
                                                src={poster}
                                                alt=""
                                                aria-hidden="true"
                                                decoding="async"
                                                fetchpriority={priority ? 'high' : 'auto'}
                                                loading={priority ? 'eager' : 'lazy'}
                                            />
                                        </picture>
                                        {showVideo && (selfHostedSrc ? (
                                            <video
                                                key={selfHostedSrc}
                                                className={videoClass}
                                                src={selfHostedSrc}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                onPlaying={() => setVideoReady(true)}
                                            />
                                        ) : (
                                            <iframe
                                                key={isMobile ? 'mobile' : 'desktop'}
                                                className={videoClass}
                                                src={isMobile ? mobileVideoUrl : videoUrl}
                                                title="YouTube video player"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                onLoad={() => setVideoReady(true)}
                                            ></iframe>
                                        ))}
                                        <div className="video-overlay"></div>
                                    </div>
                                    <div className="container-fluid">
                                        {children}
                                    </div>
                                </div>
                            </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero2;
